// src/api/adminApi.js
//
// Users, product list/search/update and user deactivation are on the Orbiee
// Admin Features API (2026-10-27); edit user, create user and enrich are
// still Cloud Functions — see the Admin block in apiConfig.
import { getAuthToken } from "../utils/auth";
import {
  ENRICH_PRODUCT_URL,
  UPDATE_ENRICH_PRODUCT_URL,
  UPDATE_PRODUCT_URL,
  UPDATE_USER_PREFERENCES_V1_URL,
  DELETE_USER_URL,
  GET_ALL_PRODUCTS_URL,
  CREATE_USER_V1_URL,
  PRODUCT_SEARCH_URL,
  USERS_URL,
} from "../config/apiConfig";

/**
 * The reason a failed admin-service call gives, else `fallback`. The service
 * is FastAPI: `detail` is a string (404) or a list of `{ msg }` (422
 * validation); `message` / `error` are kept for any other shape.
 */
const errorMessageFrom = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  const { detail } = body || {};
  const detailText = Array.isArray(detail)
    ? detail.map((d) => d?.msg).filter(Boolean).join("; ")
    : typeof detail === "string"
    ? detail
    : "";
  return body?.message || body?.error || detailText || fallback;
};

/** `{ page, page_size, total_records, total_pages, products }` → the page shape AdminPage reads. */
const toProductPage = (data, requestedPageSize) => ({
  products: Array.isArray(data?.products) ? data.products : [],
  currentPage: data?.page || 1,
  pageSize: data?.page_size || requestedPageSize || 50,
  totalPages: data?.total_pages || 1,
  totalRecords: data?.total_records || 0,
});

/**
 * Admin page's user list: `GET /users` → `{ users: [{ customer_id, user,
 * preferences }] }`. Deactivated users (`user.isActive === false`) are left
 * out: deleting a user only deactivates the record now, and the admin page
 * treats that as gone. A missing flag counts as active.
 */
export const fetchAdminUsers = async () => {
  const response = await fetch(USERS_URL, { method: "GET" });
  if (!response.ok) {
    throw new Error(
      await errorMessageFrom(response, `HTTP ${response.status}: Failed to fetch users`)
    );
  }
  const data = await response.json();
  if (!Array.isArray(data?.users)) {
    throw new Error("Unexpected user list response");
  }
  return data.users.filter((item) => item?.user?.isActive !== false);
};

export const updateUserPreferences = async (userData) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(
    UPDATE_USER_PREFERENCES_V1_URL,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      
      },
      body: JSON.stringify(userData),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: Update failed`);
  }

  return await response.json();
};

// Delete user — PUT /delete_user { customer_id } → { message, customer_id }.
// The service deactivates the record (isActive: false) rather than removing it.
export const deleteUser = async (customer_id) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(DELETE_USER_URL, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_id }),
  });

  if (!response.ok) {
    throw new Error(
      await errorMessageFrom(response, `HTTP ${response.status}: Delete failed`)
    );
  }

  return await response.json();
};

// Create new user
export const createUser = async (userData) => {
  const response = await fetch(CREATE_USER_V1_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || errorData.error || "Failed to create user"
    );
  }

  return await response.json();
};


/** `url` with `?page=&page_size=`, the paging both product endpoints take. */
const withPaging = (baseUrl, page, pageSize) => {
  const url = new URL(baseUrl);
  if (page) url.searchParams.set("page", page);
  if (pageSize) url.searchParams.set("page_size", pageSize);
  return url;
};

// Product list — GET /get_all_products?page=&page_size= (the service raises a
// page_size below 25 to 25).
export const fetchProducts = async (page = 1, pageSize = 50) => {
  const response = await fetch(withPaging(GET_ALL_PRODUCTS_URL, page, pageSize), {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(
      await errorMessageFrom(response, `Failed to fetch products: ${response.status}`)
    );
  }

  return toProductPage(await response.json(), pageSize);
};

// Enrich product description
export const enrichProduct = async (article_id, additional_prompt = "") => {
  const response = await fetch(ENRICH_PRODUCT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ article_id, additional_prompt }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: Enrich failed`);
  }
  return await response.json(); // returns { article_id, enriched_data: { ..., enriched_description } }
};

// Update enriched description
export const updateEnrichedProduct = async (article_id, enhanced_description) => {
  const response = await fetch(UPDATE_ENRICH_PRODUCT_URL, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ article_id, enhanced_description }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: Update failed`);
  }
  return await response.json(); // returns updated product
};

// Update product — PUT /update_product: `article_id` plus any product fields
// → { message, updated_article }.
export const updateProduct = async (productData) => {
  if (!productData.article_id) {
    throw new Error("article_id is required");
  }

  const response = await fetch(UPDATE_PRODUCT_URL, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(productData),
  });

  if (!response.ok) {
    throw new Error(
      await errorMessageFrom(response, `HTTP ${response.status}: Update product failed`)
    );
  }

  const result = await response.json();
  if (!result?.updated_article) {
    throw new Error("Unexpected update product response");
  }
  return result;
};

// Product search — POST /product-search?page=&page_size= with the filters
// (article_id, product_name, color, category, min_price, max_price) as the
// body. Blank fields are dropped, so an empty field never narrows the search.
export const searchProducts = async (filters, page = 1, size = 50) => {
  const body = { ...filters };
  Object.keys(body).forEach((key) => {
    if (body[key] === "" || body[key] == null) {
      delete body[key];
    }
  });

  const res = await fetch(withPaging(PRODUCT_SEARCH_URL, page, size), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(
      await errorMessageFrom(res, `Search failed: ${res.status} ${res.statusText}`)
    );
  }

  return toProductPage(await res.json(), size);
};